import router from '@/router';

import authClient from '@/services/authClient';
import CookieJar from '@/utilities/cookieJar';

import errorHandler from '@/utilities/serverErrorHandler';

export const auth = {
  namespaced: true,
  state: {
    accessToken: null,
    refreshToken: null,
    userName: null
  },

  getters: {
    isAuthenticated: state => state.accessToken !== null,
    getAccessToken: state => state.accessToken,
    getUserName: state => state.userName
  },

  mutations: {
    UPDATE_AUTH (state, payload) {
      state.accessToken = payload.accessToken;
      state.refreshToken = payload.refreshToken;
    },
    UPDATE_CURRENT_USERNAME (state, payload) {
      state.userName = payload;
    }
  },

  actions: {
    registerUser ({ commit, dispatch }, payload) {
      dispatch('ui/updateInterstitial', { isLoading: true, message: 'Creating your account...' }, { root: true });
      return authClient.post('api/user/create', payload).then((res) => {
        if (res.status === 200) {
          commit('UPDATE_CURRENT_USERNAME', payload.Mail);
          return res;
        }
      })
      .catch((err) => {
        throw errorHandler('authErrors', err);
      })
      .finally(() => {
        dispatch('ui/updateInterstitial', { isLoading: false, message: '' }, { root: true });
      });
    },
    checkUserVerified ({ commit, dispatch }, payload) {
      dispatch('ui/updateInterstitial', { isLoading: true, message: 'Checking your user...' }, { root: true });
      return authClient.get(`api/user/isuserverified/${process.env.VUE_APP_BUSINESS_UNIT}/${payload}`).then((res) => {
        if (res.status !== 200) {
          return errorHandler('verifyErrors', res);
        } else {
          if (!res.data) {
            commit('UPDATE_CURRENT_USERNAME', payload);
          }
          return res;
        }
      })
      .catch((err) => {
        return errorHandler('verifyErrors', err);
      })
      .finally(() => {
        dispatch('ui/updateInterstitial', { isLoading: false, message: '' }, { root: true });
      });
    },
    async sendVerificationEmail({ getters }, payload) {
      await new Promise(resolve => setTimeout(resolve, 5000));

      const emailPayload = {
        emailaddress: payload,
        weburl: `${process.env.VUE_APP_SITE_URL}/verify?avt=`,
        emailtemplateid: 'd-9a82b0e8958d44ba845fe60280d86611',
        senderaddress: `${process.env.VUE_APP_CONTACT_EMAIL}`,
        businessunit: process.env.VUE_APP_BUSINESS_UNIT
      };

      try {
        const res = await authClient.post('api/user/sendverificationemailforunverified', emailPayload);
        return res.status === 200 ? res : false;
      } catch (err) {
        return errorHandler('verifyErrors', err);
      }
    },
    verifyUser ({ commit, getters }, token) {
      const payload = {
        businessUnit: process.env.VUE_APP_BUSINESS_UNIT,
        token: encodeURIComponent(token)
      };
      return authClient.post('api/user/verify', payload).then((res) => {
        if (res.status === 200 || res.status === 409) {
          commit('UPDATE_CURRENT_USERNAME', null);
          return res;
        } else {
          return errorHandler('verifyErrors', res);
        }
      }).catch((err) => {
        return errorHandler('verifyErrors', err);
      });
    },
    loginUser ({ commit, dispatch }, payload) {
      dispatch('ui/updateInterstitial', { isLoading: true, message: 'Logging you in...' }, { root: true });

      // In case user didn't previously log out.
      dispatch('logout', false);

      const user = {
        username: payload.username,
        password: payload.password,
        businessUnit: payload.businessUnit
      };
      return authClient.post('api/user/login', user).then((res) => {
        if (res.status === 200) {
          const tokenExpiry = new Date().getTime() + parseInt(res.data.expires_in) * 1000;
          const refreshExpiry = new Date().getTime() + parseInt(res.data.refresh_token_expires_in, 10) * 1000;

          commit('UPDATE_AUTH', {
            accessToken: res.data.access_token,
            refreshToken: res.data.refresh_token
          });

          CookieJar.set(`${process.env.VUE_APP_COOKIE_PREFIX}access_token`, res.data.access_token, new Date(tokenExpiry));
          CookieJar.set(`${process.env.VUE_APP_COOKIE_PREFIX}refresh_token`, res.data.refresh_token, new Date(refreshExpiry));

          return true;
        }
        throw new Error('There was a problem logging in. Please try again later.');
      }).catch((err) => {
        if (err.response.status === 500 || err.response.status === 401) {
          throw new Error('Email or password is incorrect.');
        }
      }).finally(() => {
        dispatch('ui/updateInterstitial', { isLoading: false, message: '' }, { root: true });
      });
    },
    logout ({ commit, dispatch }, reload = true) {
      CookieJar.remove(`${process.env.VUE_APP_COOKIE_PREFIX}access_token`);
      CookieJar.remove(`${process.env.VUE_APP_COOKIE_PREFIX}refresh_token`);
      if (reload) {
        location.reload();
      }
    },
    redeemToken ({ commit, dispatch }, payload) {
      dispatch('ui/updateInterstitial', { isLoading: true, message: 'Checking you are logged in...' }, { root: true });
      if (payload) {
        return authClient.post('api/user/redeemaccesstoken', payload).then((res) => {
          if (res.status === 200) {
            commit('UPDATE_AUTH', {
              accessToken: res.data.access_token,
              refreshToken: res.data.refresh_token
            });

            const tokenExpiry = new Date().getTime() + parseInt(res.data.expires_in) * 1000;
            const refreshExpiry = new Date().getTime() + parseInt(res.data.refresh_token_expires_in, 10) * 1000;

            CookieJar.set(`${process.env.VUE_APP_COOKIE_PREFIX}access_token`, res.data.access_token, new Date(tokenExpiry));
            CookieJar.set(`${process.env.VUE_APP_COOKIE_PREFIX}refresh_token`, res.data.refresh_token, new Date(refreshExpiry));

            return true;
          } else {
            dispatch('logout');
            return false;
          }
        }).catch((err) => {
          dispatch('logout');
          throw new Error(errorHandler('authErrors', err));
        }).finally(() => {
          dispatch('ui/updateInterstitial', { isLoading: false, message: '' }, { root: true });
        });
      } else {
        dispatch('logout');
        this.$router.push({ name: 'Login' });
        return false;
      }
    },
    forgotPassword ({ getters }, payload) {
      const emailPayload = {
        emailaddress: payload.username,
        weburl: `${process.env.VUE_APP_SITE_URL}/reset-password?email=${payload.username}&token=`,
        emailtemplateid: 'd-16e59f8c07b94e35a987a6330114629a',
        senderaddress: `${process.env.VUE_APP_CONTACT_EMAIL}`,
        businessunit: process.env.VUE_APP_BUSINESS_UNIT
      };
      return authClient.post('api/user/sendresetpasswordemail', emailPayload).then((res) => {
        if (res.status === 200) {
          return res;
        }
        return false;
      }).catch((err) => {
        return errorHandler('verifyErrors', err);
      });
    },
    setNewPassword ({ getters }, payload) {
      const authPayload = {
        username: payload.username,
        password: payload.password,
        businessunit: process.env.VUE_APP_BUSINESS_UNIT
      };
      return authClient.put('api/user/setpasswordbysigninname', authPayload).then((res) => {
        if (res.status === 200) {
          return res;
        }
        return false;
      }).catch((err) => {
        return errorHandler('verifyErrors', err);
      });
    },
    updateAuth ({ commit }, payload) {
      commit('UPDATE_AUTH', payload);
    },
    handleUnauthorised ({ dispatch }, payload) {
      const retryFunction = payload.retryFunction;
      const retryPayload = payload.retryPayload;

      dispatch('auth/redeemToken', {
        refreshtoken: CookieJar.get(`${process.env.VUE_APP_COOKIE_PREFIX}refresh_token`),
        businessunit: process.env.VUE_APP_BUSINESS_UNIT
      }, { root: true }).then((res) => {
        if (res === true) {
          dispatch(retryFunction, retryPayload, { root: true });
          return true;
        }
				router.push({ name: 'Login' });
      }).catch(() => {
        router.push({ name: 'Login' });
      });
    }
  }
};
